import {
  normalizeShortlistImagePath,
  normalizeShortlistCandidate,
} from './storage'
import { bindShortlistSync } from './sync'
import { isShortlistPending, persistShortlistSave } from './persistence'

function imagePathFromElement(image, origin) {
  if (!(image instanceof HTMLImageElement)) return null

  const sources = [
    image.currentSrc,
    image.getAttribute('src'),
    image.dataset.src,
  ]
  for (const source of sources) {
    const imagePath = normalizeShortlistImagePath(source, origin)
    if (imagePath !== null) return imagePath
  }

  return null
}

export function candidateFromElement(root, candidateSelector, imageContainer, origin = window.location.origin) {
  const candidateElement = root.querySelector(candidateSelector)
  if (!(candidateElement instanceof HTMLScriptElement)) return null

  try {
    const candidate = JSON.parse(candidateElement.textContent || '')
    const images = Array.from(imageContainer?.querySelectorAll('img') ?? [])
    const beforeImage = images.find((image) => image.dataset.reformCaseImageRole === 'before')
    const afterImage = images.find((image) => image.dataset.reformCaseImageRole === 'after')
    if (candidate?.snapshot?.beforeImagePath == null) {
      candidate.snapshot.beforeImagePath = imagePathFromElement(beforeImage, origin)
    }
    if (candidate?.snapshot?.imagePath === null) {
      candidate.snapshot.imagePath = imagePathFromElement(afterImage ?? images[images.length - 1], origin)
    }
    return normalizeShortlistCandidate(candidate, origin)
  } catch (_error) {
    return null
  }
}

export function shortlistFailureMessage(reason) {
  return {
    'invalid-candidate': 'この事例にはいいねできませんでした。ページを再読み込みして、もう一度お試しください。',
    'storage-unavailable': 'いいね機能を利用できません。ブラウザのデータ保存設定をご確認のうえ、もう一度お試しください。',
    'unsupported-schema': 'いいねした内容を読み込めませんでした。ページを再読み込みして、もう一度お試しください。',
    'request-failed': '通信を確認できませんでした。接続をご確認のうえ、もう一度お試しください。',
  }[reason] || 'いいね状態を更新できませんでした。時間をおいてもう一度お試しください。'
}

function readToggleState(store, candidate) {
  const result = store.read()
  if (!result.ok) {
    return { ok: false, reason: result.reason, count: 0, isSaved: false }
  }
  if (candidate === null) {
    return { ok: false, reason: 'invalid-candidate', count: result.count, isSaved: false }
  }

  const isSaved = result.cases.some((entry) => entry.caseId === candidate.caseId)
  return {
    ok: true,
    count: result.count,
    isSaved,
  }
}

function likeCountFromRoot(root) {
  const element = root.querySelector('[data-reform-case-like-count]')
  if (!(element instanceof HTMLElement)) return null

  const count = Number(element.textContent)
  return Number.isSafeInteger(count) && count >= 0 ? count : null
}

export function bindShortlistToggle({ root, store, candidate, buttonSelector, render, successMessage }) {
  const button = root.querySelector(buttonSelector)
  const countElement = root.querySelector('[data-reform-case-like-count]')
  if (countElement && candidate) countElement.dataset.reactionPostId = String(candidate.caseId)
  const sync = (message = '', isError = false) => {
    const state = readToggleState(store, candidate)
    if (button instanceof HTMLButtonElement) {
      const title = root.dataset.reformCaseShortlistTitle || 'この事例'
      const count = likeCountFromRoot(root)
      state.label = count === 0 ? 'いいね！' : 'いいね'
      const countLabel = count === null ? '' : `（いいね総数${count}件）`
      const action = !state.ok ? 'にはいいねできません' : state.isSaved ? 'にいいねしました' : 'にいいねする'
      button.disabled = !state.ok || state.isSaved || isShortlistPending()
      button.setAttribute('aria-pressed', String(state.ok && state.isSaved))
      button.setAttribute('aria-label', `${title}${action}${countLabel}`)
    }
    render(root, state, message, isError)
  }

  if (button instanceof HTMLButtonElement) {
    button.addEventListener('click', async () => {
      if (candidate === null) {
        sync()
        return
      }

      if (isShortlistPending()) return
      const state = readToggleState(store, candidate)
      if (!state.ok || state.isSaved) return
      const surface = root.closest('[data-reform-case-shortlist-surface]')?.dataset.reformCaseShortlistSurface
      const result = await persistShortlistSave(store, candidate, surface)
      if (!result.ok) {
        sync(shortlistFailureMessage(result.reason), true)
        return
      }

      sync(successMessage(result))
    })
  }

  bindShortlistSync(root, sync)
}
